import { ExcludeError, PrimitiveType } from '@shared/models/type-helpers.model';
import React from 'react';

/**
 * Check if a value is a number
 * @param value
 * @returns
 */
const isNumber = (value: unknown): value is number => typeof value === 'number';

/**
 * Checks for plain object literals and will exclude arrays, classes, functions, null, built-ins
 * @param value
 * @returns
 */
const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && value.constructor === Object;

const isDefined = <T>(
  value: T | null | undefined | void,
): value is NonNullable<T> => value !== undefined && value !== null;

/**
 * Guard utils that are used to check if a value is not a certain type
 * Like guards.is.not.error(value) to check if a value is not an error
 */
const not = {
  /** Check that something is not an error */
  error: <T>(value: T): value is ExcludeError<T> =>
    value instanceof Error === false,
};

/**
 * Type guard utilities for runtime type checking with proper TypeScript narrowing
 *
 * Usage: `guards.is.number(value)`
 */
export const guards = {
  is: {
    // Primitives
    number: isNumber,
    string: (value: unknown): value is string => typeof value === 'string',
    boolean: (value: unknown): value is boolean => typeof value === 'boolean',
    undefined: (value: unknown): value is undefined => value === undefined,

    null: (value: unknown): value is null => value === null,
    /** Check if value is a primitive type (string, number, boolean, symbol, null, undefined) */
    primitive: (value: unknown): value is PrimitiveType =>
      !value || !['object', 'function'].includes(typeof value),
    /** Check if value is defined (not undefined or null) */
    defined: isDefined,

    // Objects and collections

    /** Checks for Error objects */
    error: (value: unknown): value is Error => value instanceof Error,

    /** Checks for Event */
    event: (value: unknown): value is Event => value instanceof Event,

    /** Checks for plain object literals and will exclude arrays, classes, functions, null, built-ins */
    object: isObject,

    /** Checks if a key is in an object */
    keyOf: <T extends object>(obj: T, key: string): key is string & keyof T =>
      key in obj,
    array: (value: unknown): value is unknown[] => Array.isArray(value),
    /** Checks for function objects */
    function: (value: unknown): value is (...args: unknown[]) => unknown =>
      typeof value === 'function',
    /** Checks for Promise-like (thenable) values */
    promiseLike: <T = unknown>(value: unknown): value is PromiseLike<T> => {
      if (!guards.is.defined(value)) return false;
      if (typeof value !== 'object' && typeof value !== 'function')
        return false;
      return typeof Reflect.get(value, 'then') === 'function';
    },

    // React elements
    /** Checks for valid React elements (only a proxy for convenience) */
    reactElement: (value: unknown): value is React.ReactElement<any> =>
      React.isValidElement(value),

    /** Callback ref: function receiving node */
    reactRefCallback: <T>(
      value: React.Ref<T> | null | undefined,
    ): value is React.RefCallback<T> =>
      guards.is.defined(value) && typeof value === 'function',

    /** Object ref: has a mutable `current` property */
    reactRefObject: <T>(
      value: React.Ref<T> | null | undefined,
    ): value is React.RefObject<T> =>
      guards.is.defined(value) && 'current' in value,

    // Specialized object checks
    isEmptyObject: (value: unknown): boolean =>
      isObject(value) && Object.keys(value).length === 0,

    // Specialized array checks
    /** Checks for array with at least one element, narrowing to tuple type */
    emptyArray: <T>(value: T[]): value is [] => value.length === 0,
    /** Ensures array has at least one element, narrowing to tuple type */
    nonEmptyArray: <T>(value: T[]): value is [T, ...T[]] => value.length > 0,

    // Refined number checks
    /** Checks for a number but not NaN, Infinity, -Infinity */
    finiteNumber: (value: unknown): value is number =>
      isNumber(value) && Number.isFinite(value),
    /** Checks for whole numbers (no decimals) */
    integer: (value: unknown): boolean =>
      isNumber(value) && Number.isInteger(value),
    /** Numbers greater than 0. NOTE: 0 counts as positive */
    positiveNumber: (value: unknown): boolean => isNumber(value) && value >= +0,
    /** Numbers less than 0 */
    negativeNumber: (value: unknown): boolean => isNumber(value) && value < 0,

    /** Checks if a number is between a min and max */
    numberBetween: (value: number, min: number, max: number): boolean =>
      value >= min && value <= max,

    // Refined string checks
    /** String with length 0 */
    emptyString: (value: unknown): value is '' => value === '',

    nonEmptyString: (value: unknown): boolean =>
      typeof value === 'string' && value !== '',

    /** Guard utils that are used to check if a value is not a certain type
     * Like guards.is.not.error(value) to check if a value is not an error
     */
    not,
  },
} as const;
